import { useState } from "react";
import { Copy, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import type { RuleKind } from "./kindConfig";

function truncId(id: string) {
  if (id.length <= 12) return id;
  return "…" + id.slice(-8);
}

function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function CopyBtn({ value }: { value: string }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(value);
        toast.success("Copied");
      }}
      className="opacity-60 hover:opacity-100 p-0.5"
      title={`Copy: ${value}`}
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}

function LabelRawRow({ label, raw, rawDisplay, href }: {
  label: React.ReactNode;
  raw: string;
  rawDisplay?: string;
  href?: string;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-semibold truncate">{label}</span>
        <span className="text-muted-foreground">·</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <code className="text-xs font-mono text-muted-foreground truncate">
              {rawDisplay ?? truncId(raw)}
            </code>
          </TooltipTrigger>
          <TooltipContent><span className="font-mono text-xs break-all">{raw}</span></TooltipContent>
        </Tooltip>
        <CopyBtn value={raw} />
        {href && (
          <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            className="opacity-60 hover:opacity-100">
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </TooltipProvider>
  );
}

export function WatchRuleTarget({ kind, name, config }: {
  kind: RuleKind;
  name: string;
  config: any;
}) {
  const [expanded, setExpanded] = useState(false);
  const c = config ?? {};

  switch (kind as string) {
    case "drive_folder":
      return <LabelRawRow label={c.display_name ?? name} raw={String(c.folder_id ?? "")} />;
    case "asana_project":
      return <LabelRawRow label={c.display_name ?? name} raw={String(c.project_gid ?? "")} />;
    case "gsheet_range": {
      const extras = [c.sheet_name, c.range].filter(Boolean).join("!");
      return (
        <div className="space-y-0.5">
          <LabelRawRow label={c.display_name ?? name} raw={String(c.spreadsheet_id ?? "")} />
          {extras && <div className="text-xs text-muted-foreground font-mono pl-1">{extras}</div>}
        </div>
      );
    }
    case "firecrawl_page_diff":
    case "firecrawl_sitemap_diff":
    case "firecrawl_regulator": {
      const url = String(c.url ?? "");
      return (
        <LabelRawRow
          label={c.display_name ?? name}
          raw={url}
          rawDisplay={hostOf(url)}
          href={url || undefined}
        />
      );
    }
    case "fred_series": {
      const ids: string[] = Array.isArray(c.series_ids) ? c.series_ids : [];
      const labels: Record<string, string> = c.series_labels ?? {};
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{c.display_name ?? name}</span>
            <Badge variant="outline" className="text-xs">{ids.length} series</Badge>
            {ids.length > 0 && (
              <button onClick={() => setExpanded((v) => !v)} className="opacity-60 hover:opacity-100">
                {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
            )}
          </div>
          {expanded && (
            <div className="pl-4 space-y-0.5 border-l">
              {ids.map((sid) => (
                <div key={sid} className="flex items-center gap-2 text-xs">
                  <code className="font-mono font-medium">{sid}</code>
                  {labels[sid] && <span className="text-muted-foreground truncate">— {labels[sid]}</span>}
                  <CopyBtn value={sid} />
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    case "gmail_query":
      return <LabelRawRow label={name} raw={String(c.query ?? "")} rawDisplay={String(c.query ?? "")} />;
    case "gmail_label":
      return <LabelRawRow label={name} raw={String(c.label ?? "")} rawDisplay={String(c.label ?? "")} />;
    case "calendar_keyword":
    case "otter_keyword":
    case "zoom_meeting_keyword": {
      const kws: string[] = Array.isArray(c.keywords) ? c.keywords : [];
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{name}</span>
          <span className="text-muted-foreground">·</span>
          {kws.length === 0
            ? <span className="text-xs text-muted-foreground">no keywords</span>
            : kws.map((k) => <Badge key={k} variant="outline" className="text-xs font-mono">{k}</Badge>)}
        </div>
      );
    }
    case "zoom_chat_channel":
      return <LabelRawRow label={name} raw={String(c.channel_name ?? "")} rawDisplay={String(c.channel_name ?? "")} />;
    case "zoom_ai_notes":
    case "zoom_whiteboard":
      return <span className="font-semibold">{name}</span>;
    default:
      return <span className="font-semibold">{name}</span>;
  }
}
